import { useMemo } from "react"

import { usePageShortcuts } from "@/hooks/usePageShortcuts"

const DEFAULT_NEXT_ALIASES = ["d"]
const DEFAULT_PREVIOUS_ALIASES = ["a"]
const DEFAULT_NEXT_KEY = "l"
const DEFAULT_PREVIOUS_KEY = "h"

interface UseNavigationShortcutsProps {
  enabled?: boolean
  includeHorizontalArrows?: boolean
  includeVerticalArrows?: boolean
  nextLabel?: string
  nextAliases?: string[]
  nextKey?: string
  onNext?: () => void
  onPrevious?: () => void
  previousLabel?: string
  previousAliases?: string[]
  previousKey?: string
}

export function useNavigationShortcuts({
  enabled = true,
  includeHorizontalArrows = false,
  includeVerticalArrows = false,
  nextLabel = "Next item",
  nextAliases = DEFAULT_NEXT_ALIASES,
  nextKey = DEFAULT_NEXT_KEY,
  onNext,
  onPrevious,
  previousLabel = "Previous item",
  previousAliases = DEFAULT_PREVIOUS_ALIASES,
  previousKey = DEFAULT_PREVIOUS_KEY,
}: UseNavigationShortcutsProps) {
  // Contract for linear sibling/detail navigation:
  // - horizontal movement defaults to h/l, a/d, ArrowLeft/ArrowRight
  // - collection/list surfaces should use useCollectionPageShortcuts instead,
  //   which owns the vertical row-navigation contract
  const shortcuts = useMemo(
    () => [
      ...(onNext
        ? [
            {
              action: onNext,
              command: {
                kind: "navigate" as const,
                scope: "page" as const,
              },
              key: nextKey,
              label: nextLabel,
            },
            ...nextAliases.map((key) => ({
              action: onNext,
              key,
              label: nextLabel,
              state: {
                hiddenInPalette: true,
              },
            })),
            ...(includeHorizontalArrows
              ? [
                  {
                    action: onNext,
                    key: "ArrowRight",
                    label: nextLabel,
                    state: {
                      hiddenInPalette: true,
                    },
                  },
                ]
              : []),
            ...(includeVerticalArrows
              ? [
                  {
                    action: onNext,
                    key: "ArrowDown",
                    label: nextLabel,
                    state: {
                      hiddenInPalette: true,
                    },
                  },
                ]
              : []),
          ]
        : []),
      ...(onPrevious
        ? [
            {
              action: onPrevious,
              command: {
                kind: "navigate" as const,
                scope: "page" as const,
              },
              key: previousKey,
              label: previousLabel,
            },
            ...previousAliases.map((key) => ({
              action: onPrevious,
              key,
              label: previousLabel,
              state: {
                hiddenInPalette: true,
              },
            })),
            ...(includeHorizontalArrows
              ? [
                  {
                    action: onPrevious,
                    key: "ArrowLeft",
                    label: previousLabel,
                    state: {
                      hiddenInPalette: true,
                    },
                  },
                ]
              : []),
            ...(includeVerticalArrows
              ? [
                  {
                    action: onPrevious,
                    key: "ArrowUp",
                    label: previousLabel,
                    state: {
                      hiddenInPalette: true,
                    },
                  },
                ]
              : []),
          ]
        : []),
    ],
    [
      includeHorizontalArrows,
      includeVerticalArrows,
      nextAliases,
      nextLabel,
      nextKey,
      onNext,
      onPrevious,
      previousAliases,
      previousLabel,
      previousKey,
    ],
  )

  usePageShortcuts(shortcuts, enabled)
}
